import React, {useEffect, useRef, useState} from "react";
import mapboxgl, {FullscreenControl, NavigationControl} from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css';
import jwtInterceptor from "../../service/jwtInterceptor";
import {LAND_ENDPOINTS} from "../../utils/api";

import ReactDOMServer from "react-dom/server";
import ParcelResume from "../parcelResume/ParcelResume";
import {Box, CircularProgress} from "@mui/material";
import {StyleSwitcherControl} from "../../service/map/StyleSwitcherControl";
import IconPng from '../../assets/Images/icons/285659_marker_map_icon.png'
import StarIconPng from '../../assets/Images/icons/marker-star-yellow.png'
import {landService} from "../../service/LandService";

// Variable globale pour stocker les données entre les changements de style
let persistentSearchData = {
    searchResults: null,
    myLandsResults: null
};

const LandsMapByMapBox = ({filters, onMapReady, myLandsFilter, onShowResume, mapDataRef, onResetFilters}) => {
    //MAPBOX URL/SOURCES/...
    const emptyGeoJson = {
        type: 'FeatureCollection',
        features: []
    };
    const abortControllerRef = useRef(null);
    const RESULT_SEARCH_SOURCE_ID = "search_response_source"
    const RESULT_SEARCH_POLYGONS = "search_response_polygons_layers"
    const MY_LANDS_POLYGONS = "mylands_polygons_layers"
    const RESULT_MY_LANDS_SEARCH_POLYGONS_SOURCE_ID = "my_lands_search_result_source"
    const RESULT_MY_LANDS_SEARCH_POLYGONS = "my_lands_search_result"
    const SEARCH_PAGE_SIZE = 500

    const [searchLoadingState, setSearchLoadingState] = useState(false)

    const searchLoadingRef = useRef(false);

    const setSearchLoadingRefAndState = (value) => {
        setSearchLoadingState(value)
        searchLoadingRef.current = value;
    };

    const mapRef = useRef(null);
    const mapContainerRef = useRef()
    const center = [6.255694, 46.379139]
    const zoom = 9.7
    const zoomSearchMin = 25;
    const zoomSearchMax = 30
    const [mapLoaded, setMapLoaded] = useState(false)
    const [viewState, setViewState] = useState({
        center: center,
        zoom: zoom
    });
    const [myLands, setMyLands] = useState(null)
    const [isMyLandsSourceDone, setIsMyLandsSourceDone] = useState(false)
    const [showResume, setShowResume] = useState(false);
    const [selectedLandIdResume, setSelectedLandIdResume] = useState();
    const [activeFilter, setActiveFilter] = useState({isEmpty: true})
    const [activePopup, setActivePopup] = useState(undefined)

    const [noResults, setNoResults] = useState(false);


    useEffect(() => {

        onFilter(filters)
    }, [filters]);
    useEffect(() => {

        if (myLandsFilter.tag) {

            onFilterByTag(myLandsFilter.tag)
        }
    }, [myLandsFilter]);

    useEffect(() => {
        mapboxgl.accessToken = 'pk.eyJ1Ijoic2lvb2thbWl4ZGF0YSIsImEiOiJjbTZ0ZDI1NDQwMXJpMmxyM3Z4eTZ4ejU3In0.zKpFruvjFh3Qf9mSrsnKyQ'
        mapRef.current = new mapboxgl.Map({
            container: mapContainerRef.current,
            ...viewState
        });

        let isDragging = false;
        let lastHoveredLandId = null;
        let lastHoveredLayerId = null;

        const lineLayers = [
            'all_lands-lines',
            RESULT_SEARCH_POLYGONS + '-lines',
            MY_LANDS_POLYGONS + '-lines',
            RESULT_MY_LANDS_SEARCH_POLYGONS + '-lines'
        ];

        const resetHover = () => {
            if (lastHoveredLandId && lastHoveredLayerId) {
                if (mapRef.current && mapRef.current.getLayer(lastHoveredLayerId)) {
                    // Remet l'épaisseur de la ligne à sa valeur normale (ex: 1 ou 0.5)
                    mapRef.current.setPaintProperty(lastHoveredLayerId, 'line-width', lastHoveredLayerId.includes('search') ? 2 : 1);
                }
            }
            lastHoveredLandId = null;
            lastHoveredLayerId = null;
        };

        const setHover = (landId, layerId) => {
            if (lastHoveredLandId !== landId || lastHoveredLayerId !== layerId) {
                resetHover(); // Nettoie l'ancien survol

                if (mapRef.current && mapRef.current.getLayer(layerId)) {
                    // On utilise un filtre de style ou on change l'épaisseur de la couche ciblée
                    // Note: Pour garder tous les outlines visibles tout en grossissant celui survolé,
                    // on peut appliquer un filtre d'expression d'épaisseur si besoin,
                    // ou modifier l'épaisseur globale de la couche de la parcelle.
                    mapRef.current.setPaintProperty(layerId, 'line-width', 2.5);
                }
                lastHoveredLandId = landId;
                lastHoveredLayerId = layerId;
            }
        };

        const updateCursor = () => {
            if (!mapRef.current || !mapRef.current.getCanvas) return;
            const canvas = mapRef.current.getCanvas();
            if (!canvas) return;

            if (isDragging) {
                canvas.style.cursor = 'grabbing';
            } else if (lastHoveredLandId) {
                canvas.style.cursor = 'pointer';
            } else {
                canvas.style.cursor = 'grab';
            }
        };

        mapRef.current.on('dragstart', () => {
            isDragging = true;
            resetHover();
            updateCursor();
        });

        mapRef.current.on('dragend', () => {
            isDragging = false;
            updateCursor();
        });

        let hoverTimeout;
        mapRef.current.on('mousemove', (landEvent) => {
            if (isDragging) return;

            if (hoverTimeout) {
                clearTimeout(hoverTimeout);
            }

            const features = mapRef.current.queryRenderedFeatures(landEvent.point, {
                layers: ['all_lands', RESULT_SEARCH_POLYGONS, MY_LANDS_POLYGONS, RESULT_MY_LANDS_SEARCH_POLYGONS]
            });

            if (features.length > 0) {
                const hoveredFeature = features[0];
                const hoveredLandId = hoveredFeature.properties.land_id;
                // On récupère la couche de ligne associée (ex: 'search_response_polygons_layers-lines')
                const hoveredLayerId = hoveredFeature.layer.id + '-lines';

                setHover(hoveredLandId, hoveredLayerId);
                updateCursor();
            } else {
                hoverTimeout = setTimeout(() => {
                    resetHover();
                    updateCursor();
                }, 50);
            }
        });

        mapRef.current.on('mouseleave', () => {
            if (hoverTimeout) {
                clearTimeout(hoverTimeout);
            }
            resetHover();
            updateCursor();
        });

        // Appliquer un style pour que le canvas prenne 100% de la largeur
        const setupCanvasSize = () => {
            const canvasElements = document.querySelectorAll('.mapboxgl-canvas');
            if (canvasElements.length > 0) {
                canvasElements.forEach(canvas => {
                    canvas.style.width = '100%';
                });
            }
        };

        // Fonction pour redimensionner la carte
        const handleResize = () => {
            mapRef.current.resize();
            setupCanvasSize();
        };

        // Ajouter un écouteur d'événement pour le redimensionnement de la fenêtre
        window.addEventListener('resize', handleResize);

        mapRef.current.on('load', () => {
            setupCanvasSize();
            mapRef.current.addControl(new FullscreenControl(), "bottom-left")
            mapRef.current.addControl(new NavigationControl(), "bottom-right")
            mapRef.current.addControl(new StyleSwitcherControl(), "top")
        })

        mapRef.current.on('style.load', () => {
            setupCanvasSize();
            setMapLoaded(true)
            onMapReady && onMapReady();

            // Ajout de l'écouteur de zoom
            mapRef.current.on('zoom', () => {
                const currentZoom = mapRef.current.getZoom();
                console.log('Niveau de zoom actuel:', currentZoom.toFixed(2));
            });

            restrictToSwitzerland(mapRef.current)
            mapRef.current.loadImage(IconPng, (error, image) => {
                if (error) throw error;
                if (!mapRef.current.hasImage('marker-icon-custom')) mapRef.current.addImage('marker-icon-custom', image);
            });
            mapRef.current.loadImage(StarIconPng, (error, image) => {
                if (error) throw error;
                if (!mapRef.current.hasImage('star-icon-custom')) mapRef.current.addImage('star-icon-custom', image);
            });
            buildDefaultPolygons(mapRef.current)
            fetchMyLands()
            // Recréer les sources et couches avec les données persistantes
            createSearchSourceAndLayers(mapRef.current);

            // --- AJOUT : Gérer l'opacité des polygones bleus selon le style ---
            const style = mapRef.current.getStyle();
            // On suppose que le style satellite contient 'satellite' dans son id ou nom
            const isSatellite = style && (
                (style.name && style.name.toLowerCase().includes('satellite')) ||
                (style.id && style.id.toLowerCase().includes('satellite')) ||
                (style.sprite && style.sprite.toLowerCase().includes('satellite'))
            );
            if (mapRef.current.getLayer('all_lands')) {
                mapRef.current.setPaintProperty('all_lands', 'fill-opacity', isSatellite ? 0 : 0.5);
            }
            // --- FIN AJOUT ---

            // Réappliquer les filtres si nécessaire
            if (!activeFilter.isEmpty) {
                onFilter(activeFilter);
            }
        })

        return () => {
            window.removeEventListener('resize', handleResize);
            mapRef.current.remove()
        }
    }, [])

    const restrictToSwitzerland = (map) => {
        map.addSource('country_boundaries_source', {
            type: 'vector',
            url: 'mapbox://mapbox.country-boundaries-v1',
        });

        map.addLayer({
            id: "all_countries",
            type: "fill",
            source: 'country_boundaries_source',
            "source-layer": 'country_boundaries'
        })

        // Appliquer un filtre sur la couche 'admin-1-boundaries' pour griser tous les pays
        map.setFilter('all_countries', ['!=', 'iso_3166_1', 'CH']); // 'CH' est le code ISO de la Suisse

        // Changer la couleur des autres pays en gris
        map.setPaintProperty('all_countries', 'fill-color', '#d3d3d3');
        map.setPaintProperty('all_countries', 'fill-opacity', 0.8);

    }

    const buildDefaultPolygons = (map) => {
        map.addSource('default_polygons', {
            type: 'vector',
            url: 'mapbox://siookamixdata.tileset-lands-v4',
            minzoom: 0,
            maxzoom: 22,
            cluster: false
        });

        map.addLayer({
            id: "all_lands",
            type: "fill",
            source: 'default_polygons',
            "source-layer": 'mixdata-lands',
            minzoom: 11,
            maxzoom: 22,
            paint: {
                'fill-opacity': 0.5,
                'fill-color': "#3384e9"
            }
        });

        map.addLayer({
            id: "all_lands-lines",
            type: "line",
            source: 'default_polygons',
            "source-layer": 'mixdata-lands',
            minzoom: 10,
            maxzoom: 22,
            paint: {
                'line-opacity': 0.6,
                'line-width': 0.5,
                'line-color': "#ffffff"
            }
        });

        // S'assurer que le clic sur n'importe quelle parcelle de base déclenche l'affichage et déplace l'outline
        map.on('click', 'all_lands', (landEvent) => attachPopup(landEvent));
    }

    const createSearchSourceAndLayers = (map) => {
        const layersToRemove = [
            RESULT_SEARCH_POLYGONS,
            RESULT_SEARCH_POLYGONS + '-lines',
            RESULT_MY_LANDS_SEARCH_POLYGONS,
            RESULT_MY_LANDS_SEARCH_POLYGONS + '-lines'
        ];

        layersToRemove.forEach(layerId => {
            if (map.getLayer(layerId)) {
                map.removeLayer(layerId);
            }
        });

        if (map.getSource(RESULT_SEARCH_SOURCE_ID)) {
            map.removeSource(RESULT_SEARCH_SOURCE_ID);
        }
        if (map.getSource(RESULT_MY_LANDS_SEARCH_POLYGONS_SOURCE_ID)) {
            map.removeSource(RESULT_MY_LANDS_SEARCH_POLYGONS_SOURCE_ID);
        }

        map.addSource(RESULT_SEARCH_SOURCE_ID, {
            type: 'geojson',
            data: persistentSearchData.searchResults || emptyGeoJson,
            clusterMaxZoom: 22,
            clusterRadius: 40
        });

        map.addSource(RESULT_MY_LANDS_SEARCH_POLYGONS_SOURCE_ID, {
            type: 'geojson',
            data: persistentSearchData.myLandsResults || emptyGeoJson,
            clusterMaxZoom: 22,
            clusterRadius: 40
        });

        map.addLayer({
            id: RESULT_SEARCH_POLYGONS,
            type: "fill",
            source: RESULT_SEARCH_SOURCE_ID,
            minzoom: 0,
            maxzoom: 22,
            paint: {
                'fill-opacity': 0.3,
                'fill-color': "#d3d3d3"
            }
        });

        map.addLayer({
            id: RESULT_SEARCH_POLYGONS + '-lines',
            type: "line",
            source: RESULT_SEARCH_SOURCE_ID,
            minzoom: 0,
            maxzoom: 22,
            paint: {
                'line-opacity': 1,
                'line-width': 1,
                'line-color': "#6610f2"
            }
        });

        map.addLayer({
            id: RESULT_MY_LANDS_SEARCH_POLYGONS,
            type: "fill",
            source: RESULT_MY_LANDS_SEARCH_POLYGONS_SOURCE_ID,
            minzoom: 0,
            maxzoom: 22,
            paint: {
                'fill-opacity': 0.4,
                'fill-color': "#00b03e"
            }
        });

        map.addLayer({
            id: RESULT_MY_LANDS_SEARCH_POLYGONS + '-lines',
            type: "line",
            source: RESULT_MY_LANDS_SEARCH_POLYGONS_SOURCE_ID,
            minzoom: 0,
            maxzoom: 22,
            paint: {
                'line-opacity': 1,
                'line-width': 0.5,
                'line-color': "#ffffff"
            }
        });
    }

    const fetchMyLands = () => {
        jwtInterceptor
            .post(`${LAND_ENDPOINTS.searchMyLands}`, {
                size: 1000,
                page: 0,
                toGeoJson: true
            })
            .then((res) => {
                setMyLands(res.data.data.results);
                buildMyLandLayer(res.data)
            })
            .catch((error) => {
                console.error("error in useEffect");
            });
    }

    const searchMyLands = (filters) => {
        return jwtInterceptor
            .post(`${LAND_ENDPOINTS.searchMyLands}`, {
                ...filters,
                size: 1000,
                page: 0,
                toGeoJson: true
            })
    }

    const buildMyLandLayer = (myLands) => {
        if (mapRef?.current && myLands?.data?.results && !isMyLandsSourceDone) {
            console.log('inside build myLands layer', myLands)
            mapRef.current.addSource('mylands_polygons', {
                type: 'geojson',
                data: myLands.data.results,
                minzoom: 0,
                maxzoom: 22
            });

            mapRef.current.addLayer({
                id: MY_LANDS_POLYGONS,
                type: "fill",
                source: 'mylands_polygons',
                minzoom: 0,
                maxzoom: 22,
                paint: {
                    'fill-opacity': 0.6,
                    'fill-color': "#00b03e"
                }
            })

            mapRef.current.addLayer({
                id: MY_LANDS_POLYGONS + '-lines',
                type: "line",
                source: 'mylands_polygons',
                minzoom: 0,
                maxzoom: 22,
                paint: {
                    'line-opacity': 1,
                    'line-width': 0.5,
                    'line-color': "#ffffff"
                }
            })

            mapRef.current.addLayer({
                id: 'favorites-my-lands-layer',
                type: 'symbol',
                source: "mylands_polygons",
                filter: ['==', ['get', 'isFavorite'], true],
                minzoom: 10,
                layout: {
                    'icon-image': 'star-icon-custom',
                    'icon-size': 1,
                    'icon-allow-overlap': true,
                    'text-allow-overlap': true
                }
            });

            setIsMyLandsSourceDone(true)
        }
    }

    const onShowDetails = (land_id) => {
        setShowResume(true);
        setSelectedLandIdResume(land_id);
    }

    const attachPopup = (land) => {
        if (mapRef.current) {
            const clickedFeature = land.features[0];
            const landId = clickedFeature.properties.land_id;

            // --- AJOUT : Transformer le polygone cliqué en nouveau "résultat de recherche" unique ---
            // Cela permet de déplacer l'outline coloré et le fill gris/violet sur la parcelle cliquée
            const singleFeatureGeoJson = {
                type: 'FeatureCollection',
                features: [clickedFeature]
            };
            persistentSearchData.searchResults = singleFeatureGeoJson;

            if (mapRef.current.getSource(RESULT_SEARCH_SOURCE_ID)) {
                mapRef.current.getSource(RESULT_SEARCH_SOURCE_ID).setData(singleFeatureGeoJson);
            }

            // Optionnel :rendre transparents les all_lands pour que seule la sélection ressorte (comme lors d'un filtre)
            mapRef.current.setPaintProperty('all_lands', 'fill-opacity', 0);
            // ----------------------------------------------------------------------------------

            // Fermer MapData de manière explicite
            if (mapDataRef && mapDataRef.current) {
                mapDataRef.current.closeWindow();
            }

            // Appeler onShowResume pour s'assurer que le résumé s'affiche
            if (onShowResume) {
                onShowResume();
            }

            // Fermer l'ancienne popup s'il y en a une
            if (activePopup) {
                activePopup.remove();
            }

            const popup = new mapboxgl.Popup()
                .setLngLat(land.lngLat)
                .setHTML(ReactDOMServer.renderToString(getBubbleInfo(clickedFeature.properties)))
                .addTo(mapRef.current);

            // Afficher le résumé de la parcelle
            onShowDetails(landId);
            setActivePopup(popup);
        }
    }

    const fitBoundsToSearchResponse = (map) => {
        const data = map.getSource(RESULT_SEARCH_SOURCE_ID)?._data;
        const features = data?.features ?? [];

        if (features.length === 0) {
            console.warn("No features found in the layer");
            return;
        }

        const bounds = getFeaturesBounds(features);

        if (bounds.isEmpty()) {
            map.setCenter(center);
            map.setZoom(zoom);
            return;
        }

        if (features.length === 1) {
            map.fitBounds(bounds, {
                padding: 60,
                duration: 1000,
                minZoom: zoomSearchMin,
                maxZoom: zoomSearchMax
            });

            return;
        }

        map.fitBounds(bounds, {
            padding: 30,
            duration: 1000,
            maxZoom: zoomSearchMax
        });
    };




    const getBubbleInfo = (featuresProperties) => {
        return (
            <div>
                <div>
                    <span>N° parcelle : </span> {featuresProperties.numParcelle}
                </div>
                <div>
                    <span>Commune : </span> {featuresProperties.commune}
                </div>
                <div>
                    <span>Canton : </span> {featuresProperties.canton}
                </div>
                <div>
                    <span>Adresse : </span> {featuresProperties.adresse}
                </div>
                <div>
                    <span>Nbr de batiment : </span> {featuresProperties.nbrBatiment}
                </div>
                <div>
                    <span>Année de construction : </span> {featuresProperties.anneeConstruction}
                </div>
                <div>
                    <span>Surface : </span> {featuresProperties.surface}
                </div>
                <div>
                    <span>Zone d'affectation : </span> {featuresProperties.zoneAffectation}
                </div>

            </div>
        )
    }

    const reinitializeCenter = (map) => {
        //reinitialize center
        mapRef.current.setCenter(center)
        mapRef.current.setZoom(zoom)
    }

    const reinitializeSearchResult = () => {
        if (mapRef.current) {
            reinitializeCenter(mapRef.current);

            mapRef.current.getSource(RESULT_SEARCH_SOURCE_ID).setData(emptyGeoJson);
            persistentSearchData.searchResults = emptyGeoJson;

            const lineLayers = [
                'all_lands-lines',
                RESULT_SEARCH_POLYGONS + '-lines',
                MY_LANDS_POLYGONS + '-lines',
                RESULT_MY_LANDS_SEARCH_POLYGONS + '-lines'
            ];

            lineLayers.forEach(layer => {
                if (mapRef.current.getLayer(layer)) {
                    mapRef.current.setFilter(layer, null);
                    mapRef.current.setPaintProperty(layer, 'line-width', 0.5);
                }
            });
        }
    };

    const onFilter = (filters) => {
        if (!filters) return;

        setNoResults(false);

        if (activePopup) activePopup.remove();

        setShowResume(false);
        setSelectedLandIdResume(undefined);
        onShowResume && onShowResume();

        setActiveFilter(filters);

        if (filters.isEmpty && (mapRef?.current && mapLoaded)) {
            setNoResults(false);

            mapRef.current.setPaintProperty('all_lands', 'fill-opacity', 0.8);
            mapRef.current.setPaintProperty(MY_LANDS_POLYGONS, 'fill-opacity', 0.8);

            if (mapRef.current.getSource(RESULT_SEARCH_SOURCE_ID)) {
                mapRef.current
                    .getSource(RESULT_SEARCH_SOURCE_ID)
                    .setData(emptyGeoJson);
            }

            reinitializeSearchResult();

        } else if (!filters.isEmpty && mapRef?.current && mapLoaded) {

            mapRef.current.setPaintProperty('all_lands', 'fill-opacity', 0);
            mapRef.current.setPaintProperty(MY_LANDS_POLYGONS, 'fill-opacity', 0);

            setMyLandSearchResultData(emptyGeoJson, mapRef.current);

            searchLands(filters, mapRef.current);
        }
    };


    const searchLands = async (filters, map) => {
        if (!map.getSource(RESULT_SEARCH_SOURCE_ID)) {
            createSearchSourceAndLayers(map)
        }

        reinitializeSearchResult()
        return searchLandsPartially(filters, map)
    }

    const cleanFilters = (obj) => {
        const filteredObj = {};
        Object.keys(obj).forEach(key => {
            if (
                key !== 'isEmpty' &&
                obj[key] !== undefined &&
                obj[key] !== null &&
                obj[key] !== ''
            ) {
                filteredObj[key] = obj[key];
            }
        });
        return filteredObj;
    };

    const searchLandsPartially = async (filters, map) => {
        setSearchLoadingRefAndState(true);
        setNoResults(false);

        reinitializeCenter(mapRef?.current);

        abortControllerRef.current = new AbortController();

        const apiFilters = cleanFilters(filters);

        let totalResult = 100;
        const size = SEARCH_PAGE_SIZE;
        let page = 0;
        let isFirst = true;

        const doHaveNext = (totalResult, size, nextPage) => {
            return nextPage < Math.ceil(totalResult / size);
        };

        while (
            isFirst ||
            (doHaveNext(totalResult, size, page) &&
                searchLoadingRef?.current === true)
            ) {
            if (!mapRef.current || !searchLoadingRef.current) break;

            const searchBody = {
                ...apiFilters,
                page,
                size,
                toGeoJson: true,
                geoJsonGeometryType: 'Polygon'
            };

            try {
                const data = await landService.searchLands(searchBody, {
                    signal: abortControllerRef.current.signal
                });

                if (!searchLoadingRef.current) break;

                appendResponseToResultData(data.results, map);

                totalResult = data.total;
                isFirst = false;
                page++;

            } catch (error) {
                if (error.name === 'AbortError') {
                    console.log("Recherche annulée par l'utilisateur.");
                } else {
                    console.error("Erreur API lors de la recherche:", error);
                }

                break;
            }
        }

        stopSearch();

        const searchResults =
            persistentSearchData.searchResults || emptyGeoJson;

        const hasResults =
            Array.isArray(searchResults.features) &&
            searchResults.features.length > 0;

        if (hasResults) {
            setNoResults(false);
            fitBoundsToSearchResponse(map);
        } else {
            setNoResults(true);

            map.setPaintProperty('all_lands', 'fill-opacity', 0.8);
            map.setPaintProperty(MY_LANDS_POLYGONS, 'fill-opacity', 0.8);
        }
    };


    const stopSearch = () => {
        console.log('stopping search');

        // 1. Annuler la requête HTTP en cours instantanément
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        // 2. Couper le drapeau de chargement
        setSearchLoadingRefAndState(false);

        if (mapRef.current && mapRef.current.getStyle()) {
            try {
                const source = mapRef.current.getSource(RESULT_SEARCH_SOURCE_ID);
                if (source && source._data && source._data.features && source._data.features.length > 0) {
                    fitBoundsToSearchResponse(mapRef.current);
                }
            } catch (e) {
                console.warn("Erreur silencieuse lors de la finalisation de la recherche:", e);
            }
        }
    };

    const setMyLandSearchResultData = (resultData, map) => {
        // Stocker les données dans la variable persistante
        persistentSearchData.myLandsResults = resultData;
        map.getSource(RESULT_MY_LANDS_SEARCH_POLYGONS_SOURCE_ID).setData(resultData)
    }

    const appendResponseToResultData = (resultData, map) => {
        if (!map || !map.getStyle()) {
            console.warn("Tentative de mise à jour d'une carte inexistante ou non chargée.");
            return;
        }

        try {
            const source = map.getSource(RESULT_SEARCH_SOURCE_ID);

            if (!source) {
                console.warn(`La source ${RESULT_SEARCH_SOURCE_ID} n'existe pas encore.`);
                return;
            }

            let currentData = persistentSearchData.searchResults;

            if (currentData && currentData.features) {
                const mergedFeatures = currentData.features.concat(resultData.features);
                currentData = { ...currentData, features: mergedFeatures };
            } else {
                currentData = resultData;
            }

            persistentSearchData.searchResults = currentData;
            source.setData(currentData);
        } catch (error) {
            console.error("Erreur lors de l'accès à la source Mapbox:", error);
        }
    }

    const toggleResume = () => {
        setShowResume((prevShowResume) => !prevShowResume);
    };

    const onFilterByTag = (tag) => {
        if (mapRef?.current) {
            console.log('Filtering by tag:', tag)
            reinitializeSearchResult()

            mapRef.current.setPaintProperty('all_lands', 'fill-opacity', 0)
            mapRef.current.setPaintProperty(MY_LANDS_POLYGONS, 'fill-opacity', 0)

            searchMyLands({tag: [tag._id]}).then(response => {
                if (response.data && response.data.data && response.data.data.results) {
                    const results = response.data.data.results;

                    if (results.features && results.features.length > 0) {
                        setMyLandSearchResultData(results, mapRef.current)
                        mapRef.current.setPaintProperty(RESULT_MY_LANDS_SEARCH_POLYGONS, 'fill-opacity', 0.5)
                        mapRef.current.setPaintProperty(RESULT_MY_LANDS_SEARCH_POLYGONS, 'fill-color', tag.bgColorHex)

                        const bounds = new mapboxgl.LngLatBounds();
                        results.features.forEach(feature => {
                            if (feature.geometry.type === 'Polygon' || feature.geometry.type === 'MultiPolygon') {
                                const coordinates = feature.geometry.coordinates;
                                let center;

                                if (coordinates[0] && coordinates[0][0]) {
                                    center = coordinates[0][0];
                                } else {
                                    center = [8.2275, 46.8182];
                                }

                                bounds.extend(center);
                            }
                        });

                        if (!bounds.isEmpty()) {
                            // Si une seule parcelle, zoom très rapproché
                            if (results.features.length === 1) {
                                mapRef.current.fitBounds(bounds, {
                                    padding: 20,
                                    duration: 1000
                                });

                                // Attendre la fin de l'animation de fitBounds puis ajuster au zoom exact
                                setTimeout(() => {
                                    const targetCenter = [(bounds.getEast() + bounds.getWest()) / 2, (bounds.getNorth() + bounds.getSouth()) / 2];
                                    mapRef.current.easeTo({
                                        center: targetCenter,
                                        zoom: 18.93,
                                        duration: 1000
                                    });
                                }, 1100);
                            }
                            // Si plusieurs parcelles, utiliser zoomSearchMax
                            else {
                                mapRef.current.fitBounds(bounds, {
                                    padding: 20,
                                    duration: 1000,
                                    maxZoom: zoomSearchMax
                                });
                            }
                        }
                    } else {
                        console.warn('Aucune parcelle trouvée pour le tag:', tag.name)

                        mapRef.current.setPaintProperty('all_lands', 'fill-opacity', 0.8)
                        mapRef.current.setPaintProperty(MY_LANDS_POLYGONS, 'fill-opacity', 0.8)

                        alert(`Aucune parcelle trouvée pour le tag "${tag.name}"`)
                    }
                } else {
                    console.warn('Pas de résultats pour ce tag')

                    mapRef.current.setPaintProperty('all_lands', 'fill-opacity', 0.8)
                    mapRef.current.setPaintProperty(MY_LANDS_POLYGONS, 'fill-opacity', 0.8)

                    alert(`Aucune parcelle trouvée pour le tag "${tag.name}"`)
                }
            }).catch(error => {
                console.error('Erreur lors de la recherche par tag:', error)

                mapRef.current.setPaintProperty('all_lands', 'fill-opacity', 0.8)
                mapRef.current.setPaintProperty(MY_LANDS_POLYGONS, 'fill-opacity', 0.8)

                alert(`Erreur lors de la recherche pour le tag "${tag.name}"`)
            })
        }
    }
    const getFeaturesBounds = (features) => {
        const bounds = new mapboxgl.LngLatBounds();

        features.forEach(feature => {
            const geometry = feature.geometry;

            if (!geometry) return;

            if (geometry.type === 'Polygon') {
                geometry.coordinates.forEach(ring => {
                    ring.forEach(coord => {
                        bounds.extend(coord);
                    });
                });
            }

            if (geometry.type === 'MultiPolygon') {
                geometry.coordinates.forEach(polygon => {
                    polygon.forEach(ring => {
                        ring.forEach(coord => {
                            bounds.extend(coord);
                        });
                    });
                });
            }

            if (geometry.type === 'Point') {
                bounds.extend(geometry.coordinates);
            }
        });

        return bounds;
    };


    return (
        <>
            {searchLoadingState &&
                <div className="d-flex align-items-center justify-content-center px-4 py-3 shadow-lg" style={{
                    zIndex: 9999,
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    backgroundColor: '#000000',
                    backdropFilter: 'blur(12px)',
                    WebkitBackdropFilter: 'blur(12px)',
                    borderRadius: '16px',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    color: '#ffffff',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.2)'
                }}>
                    <div className="me-3 d-flex align-items-center">
                        <CircularProgress size={40} sx={{ color: '#38bdf8' }} thickness={4} />
                    </div>

                    <div className="d-flex align-items-center">
                        <span className="fs-6 fw-normal me-3" style={{ letterSpacing: '0.3px', color: '#e2e8f0' }}>
                            Recherche des parcelles...
                        </span>
                        <div style={{ width: '1px', height: '20px', backgroundColor: 'rgba(255, 255, 255, 0.2)' }} className="me-3"></div>
                        <button
                            onClick={() => stopSearch()}
                            style={{
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                color: '#f87171',
                                fontWeight: '600',
                                fontSize: '0.95rem',
                                padding: '4px 8px',
                                borderRadius: '6px',
                                transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.backgroundColor = 'rgba(248, 113, 113, 0.15)';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.backgroundColor = 'transparent';
                            }}
                        >
                            Arrêter
                        </button>
                    </div>
                </div>
            }

            {noResults && !searchLoadingState && (
                <div className="d-flex align-items-center justify-content-center px-4 py-3 shadow-lg" style={{
                    zIndex: 9999,
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    backgroundColor: '#000000',
                    backdropFilter: 'blur(12px)',
                    WebkitBackdropFilter: 'blur(12px)',
                    borderRadius: '16px',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    color: '#ffffff',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.2)'
                }}>
                    <div className="d-flex align-items-center">
                        <span className="fs-6 fw-normal me-3" style={{ letterSpacing: '0.3px', color: '#e2e8f0' }}>
                            Aucune Parcelle ne correspond à votre recherche
                        </span>
                        <div style={{ width: '1px', height: '20px', backgroundColor: 'rgba(255, 255, 255, 0.2)' }} className="me-3"></div>
                        <button
                            onClick={() => onResetFilters?.()}
                            style={{
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                color: '#f87171',
                                fontWeight: '600',
                                fontSize: '0.95rem',
                                padding: '4px 8px',
                                borderRadius: '6px',
                                transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.backgroundColor = 'rgba(248, 113, 113, 0.15)';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.backgroundColor = 'transparent';
                            }}
                        >
                            Reinitialiser
                        </button>

                    </div>
                </div>
            )}

            <div
                id='map-container'
                style={{width: '100%', height: '100vh'}}
                ref={mapContainerRef}
                className="mapbox-container"
            />
            <style jsx="true">{`
                .mapbox-container .mapboxgl-canvas {
                    width: 100% !important;
                }
            `}</style>
            <Box className={`resume ${showResume ? "open" : "closed"}`}>

                <ParcelResume
                    closeFenetre={toggleResume}
                    landId={selectedLandIdResume}
                />
            </Box>
        </>
    )
};


export default LandsMapByMapBox;
